import React, { useMemo } from 'react';
import Select, { createFilter } from 'react-select';
import {
  ruleSelectStyles,
  ruleSelectComponents,
  clipOption,
} from './ruleSelectConfig';
import { EncompassField } from 'src/services/encompass/getEncompassFieldSchema';
import {
  ENCOMPASS_FIELD_KIND,
  getEncompassFieldKind,
  isCustomEncompassFieldKind,
  type EncompassFieldKind,
} from 'src/services/encompass/getEncompassCustomFields';
import { DocumentType } from 'src/services/documents/types';
import {
  LosFieldValueSource,
  UpdateLosFieldConfig,
} from 'src/services/loans/types';
import { cn } from 'src/utils/cn';
import { FieldKindToggle } from 'src/components/FieldKindToggle';
import { useRegisteredCustomFields } from 'src/queries/encompass/useRegisteredCustomFields';
import {
  EncompassSelectableField,
  registeredCustomFieldOptions,
} from 'src/components/admin/encompassSelectableFields';

type SelectOption = { value: string; label: string };

const DEFAULT_CONFIG: UpdateLosFieldConfig = {
  enabled: false,
  target: { kind: 'field', encompassFieldId: '' },
};

type ValueSourceType = LosFieldValueSource['type'];

const VALUE_SOURCE_OPTIONS: { value: ValueSourceType; label: string }[] = [
  { value: 'static', label: 'Static value' },
  { value: 'expression', label: 'Expression' },
  { value: 'docField', label: 'Document field' },
  { value: 'ruleResult', label: 'Rule result' },
];
const TARGET_TYPE_OPTIONS: SelectOption[] = [
  { value: 'field', label: 'Encompass field' },
  { value: 'milestone', label: 'Milestone' },
];

/** Shared input styling so native controls match across the rule builder. */
const inputClass =
  'block w-full rounded border border-gray-300 px-2 py-1.5 text-gray-700 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-xs';
const selectClass =
  'block w-full rounded border border-gray-300 py-1.5 pl-2 pr-6 text-gray-700 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-xs';

/** Faster filter for large option sets — skips per-keystroke accent stripping. */
const fastFilter = createFilter({ ignoreAccents: false });

function SectionLabel({ children }: Readonly<{ children: React.ReactNode }>) {
  return <span className="text-sm font-medium text-gray-900">{children}</span>;
}

/** Searchable dropdown whose options are always sorted ascending by label. */
function SearchableSelect({
  ariaLabel,
  inputId,
  value,
  options,
  onChange,
  placeholder,
  isDisabled,
}: Readonly<{
  ariaLabel: string;
  inputId: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  placeholder: string;
  isDisabled?: boolean;
}>) {
  const sortedOptions = useMemo(() => {
    const seen = new Set<string>();
    const unique = options.filter((option) => {
      if (seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });
    return unique.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
  }, [options]);

  return (
    <Select<SelectOption>
      aria-label={ariaLabel}
      inputId={inputId}
      isDisabled={isDisabled}
      isClearable
      options={sortedOptions}
      value={sortedOptions.find((option) => option.value === value) ?? null}
      onChange={(option) => onChange(option?.value ?? '')}
      placeholder={placeholder}
      className="text-xs"
      classNamePrefix="los-value-select"
      components={ruleSelectComponents}
      filterOption={fastFilter}
      menuPortalTarget={typeof document === 'undefined' ? null : document.body}
      styles={{
        option: clipOption,
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      }}
    />
  );
}

function ValueSourceEditor({
  idPrefix,
  label,
  accent,
  value,
  onChange,
  selectedField,
  docTypeOptions,
  fieldsByDocType,
}: Readonly<{
  idPrefix: string;
  label: string;
  accent: 'pass' | 'fail';
  value: LosFieldValueSource | undefined;
  onChange: (next: LosFieldValueSource | undefined) => void;
  selectedField: EncompassSelectableField | undefined;
  docTypeOptions: SelectOption[];
  fieldsByDocType: Record<string, SelectOption[]>;
}>) {
  const enabled = Boolean(value);
  const type: ValueSourceType = value?.type ?? 'static';

  const handleTypeChange = (nextType: ValueSourceType) => {
    if (nextType === 'static') onChange({ type: 'static', value: '' });
    else if (nextType === 'expression')
      onChange({ type: 'expression', expression: '' });
    else if (nextType === 'ruleResult') onChange({ type: 'ruleResult' });
    else onChange({ type: 'docField', documentType: '', field: '' });
  };

  const checkboxOptions = selectedField?.options ?? [];

  const enabledAccentClass =
    accent === 'pass'
      ? 'border-green-200 bg-green-50/40'
      : 'border-red-200 bg-red-50/40';

  return (
    <div
      className={cn(
        'flex flex-col gap-1.5 rounded border p-2 transition-colors',
        enabled ? enabledAccentClass : 'border-gray-200',
      )}
    >
      <div className="flex items-center justify-between">
        <label
          htmlFor={`${idPrefix}-enable`}
          className="flex items-center gap-1.5 text-xs font-medium text-gray-700"
        >
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              accent === 'pass' ? 'bg-green-500' : 'bg-red-500',
            )}
          />
          {label}
        </label>
        <input
          id={`${idPrefix}-enable`}
          type="checkbox"
          checked={enabled}
          aria-label={`Write to LOS ${label.toLowerCase()}`}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onChange(
              event.currentTarget.checked
                ? { type: 'static', value: '' }
                : undefined,
            )
          }
          className="h-3.5 w-3.5 cursor-pointer accent-blue-600"
        />
      </div>

      {enabled && (
        <div className="flex flex-col gap-1.5">
          <select
            aria-label={`${label} value source`}
            value={type}
            onChange={(event: React.ChangeEvent<HTMLSelectElement>) =>
              handleTypeChange(event.currentTarget.value as ValueSourceType)
            }
            className={selectClass}
          >
            {VALUE_SOURCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          {value?.type === 'static' &&
            (checkboxOptions.length > 0 ? (
              <SearchableSelect
                ariaLabel={`${label} static value`}
                inputId={`${idPrefix}-static-value`}
                value={value.value}
                options={checkboxOptions.map((opt) => ({
                  value: opt.value,
                  label: opt.text || opt.value,
                }))}
                onChange={(next) => onChange({ type: 'static', value: next })}
                placeholder="Select…"
              />
            ) : (
              <input
                type="text"
                aria-label={`${label} static value`}
                value={value.value}
                placeholder="Value to write"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  onChange({ type: 'static', value: event.currentTarget.value })
                }
                className={inputClass}
              />
            ))}

          {value?.type === 'expression' && (
            <input
              type="text"
              aria-label={`${label} expression`}
              value={value.expression}
              placeholder="e.g. [W2.EmployerName]"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onChange({
                  type: 'expression',
                  expression: event.currentTarget.value,
                })
              }
              className={inputClass}
            />
          )}

          {value?.type === 'docField' && (
            <div className="grid grid-cols-2 gap-1.5">
              <SearchableSelect
                ariaLabel={`${label} document type`}
                inputId={`${idPrefix}-doc-type`}
                value={value.documentType}
                options={docTypeOptions}
                onChange={(next) =>
                  onChange({
                    type: 'docField',
                    documentType: next,
                    field: '',
                  })
                }
                placeholder="Document type…"
              />
              <SearchableSelect
                ariaLabel={`${label} document field`}
                inputId={`${idPrefix}-doc-field`}
                value={value.field}
                isDisabled={!value.documentType}
                options={fieldsByDocType[value.documentType] ?? []}
                onChange={(next) =>
                  onChange({
                    type: 'docField',
                    documentType: value.documentType,
                    field: next,
                  })
                }
                placeholder="Field…"
              />
            </div>
          )}

          {value?.type === 'ruleResult' && (
            <p className="text-[10px] text-gray-500">
              Writes the rule&apos;s evaluated result to this field.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export interface UpdateLosFieldEditorProps {
  /** Current config from the rule definition (`definition.updateLosField`). */
  config: UpdateLosFieldConfig | undefined;
  /** Persist the next config (or `undefined` to clear it). */
  onChange: (next: UpdateLosFieldConfig | undefined) => void;
  /** Encompass field schema (writable + read-only; filtered here). */
  encompassFields: EncompassField[];
  isFieldsLoading: boolean;
  isFieldsError: boolean;
  /** Tenant document types, used for the "Document field" value source. */
  documentTypes: DocumentType[];
}

/**
 * Presentational "Update LOS Field" rule-action editor (MVP-2995).
 *
 * Pure/props-driven so it can be hosted by both the loan-level rule builder
 * (store-bound wrapper, see UpdateLosFieldAction) and the admin checklist
 * management rule builder (local draft state, see RuleManagement).
 */
export function UpdateLosFieldEditor({
  config,
  onChange,
  encompassFields,
  isFieldsLoading,
  isFieldsError,
  documentTypes,
}: Readonly<UpdateLosFieldEditorProps>) {
  const enabled = config?.enabled ?? false;
  const targetFieldKind: EncompassFieldKind =
    config?.target.kind === 'field'
      ? getEncompassFieldKind(config.target.fieldKind)
      : ENCOMPASS_FIELD_KIND.STANDARD;
  const customFieldsQuery = useRegisteredCustomFields(
    enabled &&
      config?.target.kind === 'field' &&
      isCustomEncompassFieldKind(targetFieldKind),
  );

  const writableFields = useMemo(() => {
    const seen = new Set<string>();
    return encompassFields.filter((f) => {
      if (f.readOnly || seen.has(f.id)) return false;
      seen.add(f.id);
      return true;
    });
  }, [encompassFields]);

  const standardSelectableFields = useMemo<EncompassSelectableField[]>(
    () =>
      writableFields.map((field) => ({
        ...field,
        fieldKind: ENCOMPASS_FIELD_KIND.STANDARD,
      })),
    [writableFields],
  );

  const customSelectableFields = useMemo(
    () => registeredCustomFieldOptions(customFieldsQuery.data),
    [customFieldsQuery.data],
  );

  const targetFields = isCustomEncompassFieldKind(targetFieldKind)
    ? customSelectableFields
    : standardSelectableFields;

  const fieldOptions = useMemo<SelectOption[]>(
    () =>
      targetFields.map((f) => ({
        value: f.id,
        label: f.description ? `${f.id} — ${f.description}` : f.id,
      })),
    [targetFields],
  );

  const selectedFieldId =
    config?.target.kind === 'field' ? config.target.encompassFieldId : '';

  const selectedField = useMemo(
    () => targetFields.find((f) => f.id === selectedFieldId),
    [targetFields, selectedFieldId],
  );

  const docTypeOptions = useMemo<SelectOption[]>(
    () =>
      documentTypes.map((dt) => ({
        value: dt.displayName,
        label: dt.displayName,
      })),
    [documentTypes],
  );

  const fieldsByDocType = useMemo<Record<string, SelectOption[]>>(() => {
    const map: Record<string, SelectOption[]> = {};
    documentTypes.forEach((dt) => {
      map[dt.displayName] = dt.fields.map((field) => ({
        value: field.displayName,
        label: field.displayName,
      }));
    });
    return map;
  }, [documentTypes]);

  const patchConfig = (partial: Partial<UpdateLosFieldConfig>) => {
    const base = config ?? DEFAULT_CONFIG;
    onChange({ ...base, ...partial });
  };

  const targetFieldLoading = isCustomEncompassFieldKind(targetFieldKind)
    ? customFieldsQuery.isFetching
    : isFieldsLoading;
  const targetFieldError = isCustomEncompassFieldKind(targetFieldKind)
    ? customFieldsQuery.isError
    : isFieldsError;

  const toggleEnabled = () => {
    if (enabled) {
      onChange({ ...(config ?? DEFAULT_CONFIG), enabled: false });
    } else {
      onChange({ ...DEFAULT_CONFIG, ...config, enabled: true });
    }
  };

  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded border p-2.5',
        enabled ? 'border-blue-200 bg-blue-50/30' : 'border-gray-200',
      )}
    >
      <div className="flex flex-row items-center justify-between">
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-700">
            Update LOS Field
          </span>
          <span className="text-[10px] text-gray-500">
            Write rule outcome to Encompass
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Enable update LOS field"
          className={cn(
            'relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none',
            enabled ? 'bg-blue-600' : 'bg-gray-200',
          )}
          onClick={toggleEnabled}
        >
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
              enabled ? 'translate-x-3' : 'translate-x-0',
            )}
          />
        </button>
      </div>

      {enabled && config && (
        <div className="flex flex-col gap-3 border-t border-blue-100 pt-2.5">
          {/* Target */}
          <div className="flex flex-col gap-1.5">
            <SectionLabel>Target</SectionLabel>
            <div className="flex flex-col gap-1.5">
              <Select<SelectOption>
                aria-label="LOS target type"
                inputId="los-target-type"
                isSearchable={false}
                value={
                  TARGET_TYPE_OPTIONS.find(
                    (option) => option.value === config.target.kind,
                  ) ?? null
                }
                options={TARGET_TYPE_OPTIONS}
                onChange={(option) =>
                  patchConfig({
                    target:
                      option?.value === 'milestone'
                        ? { kind: 'milestone', milestone: '' }
                        : { kind: 'field', encompassFieldId: '' },
                  })
                }
                className="text-xs"
                classNamePrefix="los-target-type-select"
                components={ruleSelectComponents}
                menuPortalTarget={
                  typeof document === 'undefined' ? null : document.body
                }
                menuPosition="fixed"
                menuPlacement="auto"
                maxMenuHeight={220}
                styles={ruleSelectStyles}
              />

              {config.target.kind === 'milestone' ? (
                <input
                  type="text"
                  aria-label="Milestone name"
                  value={config.target.milestone}
                  placeholder="Milestone name"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    patchConfig({
                      target: {
                        kind: 'milestone',
                        milestone: event.currentTarget.value,
                      },
                    })
                  }
                  className={inputClass}
                />
              ) : (
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center">
                    <FieldKindToggle
                      value={targetFieldKind}
                      onChange={(kind) =>
                        patchConfig({
                          target: {
                            kind: 'field',
                            encompassFieldId: '',
                            ...(isCustomEncompassFieldKind(kind)
                              ? { fieldKind: kind }
                              : {}),
                          },
                          onTrue: undefined,
                          onFalse: undefined,
                        })
                      }
                      ariaLabel="Field type"
                      size="sm"
                    />
                  </div>
                  <Select<SelectOption>
                    aria-label="Encompass field"
                    inputId="los-encompass-field"
                    isLoading={targetFieldLoading}
                    options={fieldOptions}
                    value={
                      fieldOptions.find((o) => o.value === selectedFieldId) ??
                      null
                    }
                    onChange={(option) =>
                      patchConfig({
                        target: {
                          kind: 'field',
                          encompassFieldId: option?.value ?? '',
                          ...(isCustomEncompassFieldKind(targetFieldKind)
                            ? { fieldKind: targetFieldKind }
                            : {}),
                        },
                      })
                    }
                    placeholder="Search field…"
                    className="text-xs"
                    classNamePrefix="los-field-select"
                    components={ruleSelectComponents}
                    filterOption={fastFilter}
                    menuPortalTarget={
                      typeof document === 'undefined' ? null : document.body
                    }
                    menuPosition="fixed"
                    menuPlacement="auto"
                    maxMenuHeight={220}
                    styles={ruleSelectStyles}
                  />
                  {isCustomEncompassFieldKind(targetFieldKind) &&
                    customFieldsQuery.isSuccess &&
                    targetFields.length === 0 && (
                      <p className="text-[10px] text-gray-500">
                        Add a custom field through Admin Mapping first.
                      </p>
                    )}
                </div>
              )}
            </div>

            {targetFieldError && (
              <p role="alert" className="text-[10px] text-red-600">
                Failed to load Encompass fields.
              </p>
            )}
          </div>

          {/* Per-outcome value sources */}
          <div className="flex flex-col gap-1.5">
            <SectionLabel>Values to write</SectionLabel>
            <ValueSourceEditor
              idPrefix="los-on-true"
              label="On Pass"
              accent="pass"
              value={config.onTrue}
              onChange={(next) => patchConfig({ onTrue: next })}
              selectedField={selectedField}
              docTypeOptions={docTypeOptions}
              fieldsByDocType={fieldsByDocType}
            />
            <ValueSourceEditor
              idPrefix="los-on-false"
              label="On Fail"
              accent="fail"
              value={config.onFalse}
              onChange={(next) => patchConfig({ onFalse: next })}
              selectedField={selectedField}
              docTypeOptions={docTypeOptions}
              fieldsByDocType={fieldsByDocType}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default UpdateLosFieldEditor;
